/**
 * NaamDarbar — Firebase Client Configuration (LAZY)
 *
 * Firebase JS SDK weighs ~165 kB (~33 kB gzipped) and most of our visitors
 * never sign in — there's no reason to ship it on first paint.
 *
 * This module exports `getFirebaseAuth()` which dynamic-imports `firebase/app`
 * and `firebase/auth` on demand, and caches the resulting `{ auth, googleProvider, mod }`
 * promise so subsequent callers reuse the same SDK instance.
 *
 * A small localStorage flag (`kd:was-authed`) is set on successful sign-in
 * and cleared on sign-out, so:
 *   - First-time anonymous visitors NEVER load firebase.
 *   - Returning signed-in users trigger the lazy load on first paint.
 *   - Anonymous visitors who click "Sign In" trigger it on demand.
 */

import type {
  Auth,
  GoogleAuthProvider as GoogleAuthProviderType,
  User,
} from 'firebase/auth';

const WAS_AUTHED_KEY = 'kd:was-authed';

export function wasAuthed(): boolean {
  try {
    return localStorage.getItem(WAS_AUTHED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markAuthed(): void {
  try {
    localStorage.setItem(WAS_AUTHED_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function clearAuthed(): void {
  try {
    localStorage.removeItem(WAS_AUTHED_KEY);
  } catch {
    /* ignore */
  }
}

// Re-export the User type as a type-only export so consumers don't pull in
// the SDK at module-import time.
export type { User };

interface FirebaseBundle {
  auth: Auth;
  googleProvider: GoogleAuthProviderType;
  // Re-export the auth module so callers can get fresh references to
  // `onAuthStateChanged`, `signInWithPopup`, etc., without a separate import
  // (and without forcing them to be in the initial bundle).
  mod: typeof import('firebase/auth');
}

let _bundle: Promise<FirebaseBundle> | null = null;

/**
 * Lazily load + initialise Firebase. Memoised — subsequent calls return the
 * same promise (and therefore the same Auth instance).
 */
export function getFirebaseAuth(): Promise<FirebaseBundle> {
  if (_bundle) return _bundle;
  _bundle = (async () => {
    const [{ initializeApp }, mod] = await Promise.all([
      import('firebase/app'),
      import('firebase/auth'),
    ]);
    const app = initializeApp({
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    });
    const auth = mod.getAuth(app);
    const googleProvider = new mod.GoogleAuthProvider();
    return { auth, googleProvider, mod };
  })();
  return _bundle;
}

